import {
  ABSTRACTION_STEP_ORDER,
  ABSTRACT_REQUEST_STATUSES,
} from "../../constants/abstraction-statuses.js";
import {
  CUSTOM_TERMS,
  TRANSACTION_DETAILS,
  BUILDING_DETAILS,
  COMMERCIAL_TERMS,
  RIGHTS_AND_OPTIONS,
} from "../../constants/abstract-summary-config.js";
import * as tags from "badlands-shared/constants";
import { TAG_EDIT_ACTIONS } from "../../constants/tag-edit-actions.js";
import { convertSnakeToCamel } from "../string-utils/string-utils.js";

export const isStepComplete = (step, currentStatus) => {
  return (
    !!step &&
    !!currentStatus &&
    (currentStatus === ABSTRACT_REQUEST_STATUSES.COMPLETED ||
      currentStatus === ABSTRACT_REQUEST_STATUSES.FAILED ||
      (ABSTRACTION_STEP_ORDER.includes(step) &&
        ABSTRACTION_STEP_ORDER.includes(currentStatus) &&
        ABSTRACTION_STEP_ORDER.indexOf(step) <
          ABSTRACTION_STEP_ORDER.indexOf(currentStatus)))
  );
};

export const requestProcessing = (abstractRequest) => {
  return (
    abstractRequest.status !== ABSTRACT_REQUEST_STATUSES.COMPLETED &&
    abstractRequest.status !== ABSTRACT_REQUEST_STATUSES.FAILED
  );
};

export const transformTagList = (tagList = []) => {
  return {
    cols: [
      {
        tags: tagList.map((term) => ({
          name: term.tagName,
        })),
      },
    ],
  };
};

const createSummaryLayout = () => {
  return {
    [TRANSACTION_DETAILS]: {
      cols: [
        {
          tags: [
            { name: tags.TRANSACTION_TYPE },
            { name: tags.TENANT_LEGAL_NAME },
            { name: tags.LANDLORD_LEGAL_NAME },
            { name: tags.LANDLORD_BROKER },
            { name: tags.TENANT_BROKER },
            { name: tags.GUARANTOR },
            { name: tags.ESTOPPEL },
          ],
        },
      ],
    },
    [BUILDING_DETAILS]: {
      cols: [
        {
          tags: [
            { name: tags.BUILDING_ADDRESS },
            { name: tags.SUITE_NUMBERS },
            { name: tags.RENTABLE_SQUARE_FOOTAGE },
          ],
        },
      ],
    },
    [COMMERCIAL_TERMS]: {
      cols: [
        {
          tags: [
            { name: tags.EFFECTIVE_DATE },
            { name: tags.COMMENCEMENT_DATE },
            { name: tags.TERM },
            { name: tags.EXPIRATION_DATE },
            { name: tags.TENANT_IMPROVEMENT },
            { name: tags.RENT_ABATEMENT },
            { name: tags.TAX_ABATEMENT },
          ],
        },
        {
          tags: [
            { name: tags.RENT_COMMENCEMENT_DATE },
            { name: tags.BASE_RENT_SCHEDULE },
            { name: tags.PERCENT_RENT },
            { name: tags.SECURITY_DEPOSITY_AMOUNT },
            { name: tags.LETTER_OF_CREDIT_AMOUNT },
            { name: tags.LATE_FEE },
            { name: tags.PRO_RATA_SHARE },
            { name: tags.OPEX_PRO_RATA_SHARE },
            { name: tags.TAX_PRO_RATA_SHARE },
          ],
        },
      ],
    },
    [RIGHTS_AND_OPTIONS]: {
      cols: [
        {
          tags: [
            { name: tags.RIGHT_TO_SUBLET },
            { name: tags.RIGHT_OF_FIRST_OFFER },
            { name: tags.RIGHT_OF_FIRST_REFUSAL },
            { name: tags.PARKING },
            { name: tags.LANDLORD_RELOCATION },
            { name: tags.EXPANSION },
            { name: tags.EXCLUSIVE_USE },
            { name: tags.PURCHASE_RIGHT },
            { name: tags.RENEWAL_OPTION },
            { name: tags.EARLY_TERMINATION },
            { name: tags.LANDLORD_TERMINATION },
            { name: tags.CONTRACTION },
            { name: tags.PERMITTED_USE },
            { name: tags.PROHIBITED_USE },
            { name: tags.SIGNAGE },
            { name: tags.TELECOM },
          ],
        },
      ],
    },
  };
};

export const getSummaryLayout = (customTerms, manualTags) => {
  const layout = {
    ...createSummaryLayout(),
    [CUSTOM_TERMS]: transformTagList(customTerms),
  };

  manualTags?.forEach((manualTag) => {
    layout[manualTag.section]?.cols[0].tags.push({
      name: manualTag.tagName,
    });
  });
  return layout;
};

export const getComposedGroups = (customTerms, manualTags = []) => {
  const summaryLayout = getSummaryLayout(customTerms, manualTags);
  const groups = Object.keys(summaryLayout).map((key) => {
    const group = summaryLayout[key];
    const tags = group.cols.reduce((acc, col) => {
      return acc.concat(col.tags);
    }, []);
    return {
      name: key,
      tags: tags.map((tag) => tag.name || tag.tagName),
    };
  });

  const standardTags = groups.filter((group) => group.name !== CUSTOM_TERMS);
  const customTags = groups.filter((group) => group.name === CUSTOM_TERMS);
  const allTags = groups;

  return {
    standardTags,
    customTags,
    allTags,
  };
};

export const applyTagEdits = (abstract, tagEdits, t) => {
  if (!abstract || !abstract.tags) {
    return abstract;
  }
  const updatedAbstract = { ...abstract, tags: { ...abstract.tags } };
  Object.entries(updatedAbstract.tags).forEach(([key, tag]) => {
    const tagEdit = tagEdits[key]?.[0]; //only interested in latest edit for each tag
    if (!tagEdit || tagEdit.action === TAG_EDIT_ACTIONS.RESET) {
      //if no edit or is a reset action, keep the original value
      return;
    }
    if (tagEdit.action === TAG_EDIT_ACTIONS.DELETE) {
      updatedAbstract.tags[key] = applyEdit(tag, {
        content: { value: t("deletedByUser"), sources: [], processedValue: {} },
        action: tagEdit.action,
        createdAt: tagEdit.createdAt,
        UserId: tagEdit.UserId,
      });
    } else if (tagEdit.action === TAG_EDIT_ACTIONS.EDIT) {
      updatedAbstract.tags[key] = applyEdit(tag, tagEdit);
    }
  });
  return updatedAbstract;
};

export const applyHistoricalTagEdits = (abstract, tagEdits, t) => {
  if (!abstract || !abstract.tags) {
    return abstract;
  }
  const updatedAbstract = { ...abstract, tags: { ...abstract.tags } };
  Object.entries(updatedAbstract.tags).forEach(([key, tag]) => {
    if (tag.historical?.length) {
      tag.historical.forEach((historyItem, index) => {
        const tagEdit = tagEdits[key]?.find(
          (edit) => edit.historyIndex === index
        );
        if (!tagEdit || tagEdit.action === TAG_EDIT_ACTIONS.RESET) {
          //if no edit or is a reset action, keep the original value
          return;
        }
        if (tagEdit.action === TAG_EDIT_ACTIONS.DELETE) {
          updatedAbstract.tags[key].historical[index] = applyHistoricalEdit(
            historyItem,
            {
              content: {
                value: t("deletedByUser"),
                source: {},
                processedValue: {},
              },
              action: tagEdit.action,
              createdAt: tagEdit.createdAt,
              UserId: tagEdit.UserId,
            }
          );
        } else if (tagEdit.action === TAG_EDIT_ACTIONS.EDIT) {
          updatedAbstract.tags[key].historical[index] = applyHistoricalEdit(
            historyItem,
            tagEdit
          );
        }
      });
    }
  });
  return updatedAbstract;
};

export const applyManualTagEdits = (manualTags, tagEdits, t) => {
  if (!manualTags || !manualTags.length) {
    return manualTags;
  }
  const updatedManualTags = manualTags
    .map((manualTag) => manualTag.dataValues || manualTag)
    .map((tag) => {
      const tagEdit = tagEdits[tag.tagName]?.[0]; //only interested in latest edit for each tag
      if (!tagEdit || tagEdit.action === TAG_EDIT_ACTIONS.RESET) {
        //if no edit or is a reset action, keep the original value
        return { ...tag };
      }
      if (tagEdit.action === TAG_EDIT_ACTIONS.DELETE) {
        return {
          ...tag,
          content: {
            ...tag.content,
            value: t("deletedByUser"),
            sources: [],
          },
          processedValue: {},
          editedAt: tagEdit.createdAt,
          editedBy: tagEdit.UserId,
          action: tagEdit.action,
        };
      } else if (tagEdit.action === TAG_EDIT_ACTIONS.EDIT) {
        return {
          ...tag,
          content: { ...tag.content, ...tagEdit.content },
          processedValue: {},
          editedAt: tagEdit.createdAt,
          editedBy: tagEdit.UserId,
          action: tagEdit.action,
        };
      }
    });
  return updatedManualTags;
};

const applyEdit = (tag, tagEdit) => {
  const { content, createdAt, UserId, action } = tagEdit;
  if (Array.isArray(tag)) {
    return [
      { ...tag[0], ...content, editedAt: createdAt, editedBy: UserId, action },
    ];
  } else if (typeof tag === "object") {
    return {
      ...tag,
      current: [
        {
          ...(tag.current?.[0] || {}),
          ...content,
          editedAt: createdAt,
          editedBy: UserId,
          action: action,
        },
      ],
    };
  } else {
    return tag; //if tag is not an array or object, not sure what it is so return as is
  }
};

const applyHistoricalEdit = (tagHistoryEntry, tagHistoryEdit) => {
  const { content, createdAt, UserId, action, historyIndex } = tagHistoryEdit;
  return {
    ...tagHistoryEntry,
    ...content,
    editedAt: createdAt,
    editedBy: UserId,
    action: action,
    historyIndex: historyIndex,
  };
};

export const parseProcessedValue = (tag) => {
  const processed = { isProcessedValue: false, value: "" };
  if (!tag) return processed;
  const { summary, ...subTerms } = tag.processedValue || {};
  const hasProcessedValue = summary && "value" in summary;
  const processedValue = summary?.value;
  if (tag.action && tag.action === TAG_EDIT_ACTIONS.DELETE) {
    return { isProcessedValue: false, ...tag };
  } else if (hasProcessedValue) {
    processed.isProcessedValue = true;
    processed.value = processedValue;
  } else {
    processed.value = tag.value;
  }

  if (subTerms) {
    processed.subTerms = { ...subTerms };
  }

  return processed;
};

export const parseSourceValueArray = (values) => {
  if (typeof values === "string") {
    try {
      const jsonStr = values.replace(/'/g, '"');
      values = JSON.parse(jsonStr);
      Object.entries(values).forEach(([key, value]) => {
        values[key] = convertSnakeToCamel(value);
      });
      return values;
    } catch (error) {
      console.error(
        `Error parsing rent schedule value as JSON: ${error.message}`,
        { values }
      );
      return values;
    }
  }
  return values;
};

export const documentsWereSplit = (abstractRequest) => {
  return abstractRequest.Documents.some((doc) => !!doc.parentDocId);
};

export const getDefaultBaseRentScheduleRow = () => {
  return {
    headers: ["", "", ""],
    rows: [
      {
        id: `row-${Date.now()}`,
        values: ["", "", ""],
      },
    ],
  };
};
